import type {
  EnemyInstance,
  GridCoordinate,
  NavigationSnapshot,
  PlacementResult,
} from "../domain/types";
import { coordinateKey, GridManager } from "./grid";

const DIRECTIONS: readonly GridCoordinate[] = [
  { x: 0, y: -1 },
  { x: 1, y: 0 },
  { x: 0, y: 1 },
  { x: -1, y: 0 },
];
interface QueueNode {
  readonly coordinate: GridCoordinate;
  readonly distance: number;
  readonly order: number;
}

class MinQueue {
  private readonly nodes: QueueNode[] = [];
  push(node: QueueNode): void {
    this.nodes.push(node);
    let index = this.nodes.length - 1;
    while (index > 0) {
      const parent = Math.floor((index - 1) / 2);
      if (this.compare(this.nodes[parent]!, node) <= 0) break;
      this.nodes[index] = this.nodes[parent]!;
      index = parent;
    }
    this.nodes[index] = node;
  }
  pop(): QueueNode | undefined {
    const first = this.nodes[0],
      tail = this.nodes.pop();
    if (!first || !tail || this.nodes.length === 0) return first;
    let index = 0;
    while (true) {
      const left = index * 2 + 1,
        right = left + 1;
      if (left >= this.nodes.length) break;
      const child =
        right < this.nodes.length && this.compare(this.nodes[right]!, this.nodes[left]!) < 0
          ? right
          : left;
      if (this.compare(this.nodes[child]!, tail) >= 0) break;
      this.nodes[index] = this.nodes[child]!;
      index = child;
    }
    this.nodes[index] = tail;
    return first;
  }
  get size(): number {
    return this.nodes.length;
  }
  private compare(a: QueueNode, b: QueueNode): number {
    return a.distance - b.distance || a.order - b.order;
  }
}

/** Reverse Dijkstra field shared by every runner. Rebuilt only when occupancy changes. */
export class NavigationManager {
  private revision = 0;
  private snapshotValue: NavigationSnapshot;
  constructor(readonly grid: GridManager) {
    this.snapshotValue = this.calculate();
  }
  get snapshot(): NavigationSnapshot {
    return this.snapshotValue;
  }

  recalculate(): NavigationSnapshot {
    this.revision += 1;
    this.snapshotValue = this.calculate();
    return this.snapshotValue;
  }

  simulatePlacement(
    blockedCells: readonly GridCoordinate[],
    activeEnemies: readonly EnemyInstance[] = [],
  ): PlacementResult {
    const blocked = new Set(blockedCells.map(coordinateKey)),
      navigation = this.calculate(blocked, this.revision + 1);
    if (navigation.reachableEntrances.length !== this.grid.map.entrances.length)
      return {
        valid: false,
        reason: "That tower would seal the only route to Lion Gate.",
        cells: blockedCells,
      };
    for (const enemy of activeEnemies.filter((entry) => entry.state === "active")) {
      const occupiedByRunner = [
        enemy.cell,
        enemy.targetCell,
        { x: Math.floor(enemy.position.x), y: Math.floor(enemy.position.y) },
      ].some((coordinate) => coordinate && blocked.has(coordinateKey(coordinate)));
      if (occupiedByRunner)
        return {
          valid: false,
          reason: "A runner is occupying or entering that space.",
          cells: blockedCells,
        };
      if (!this.hasNearbyReachableCell(enemy.cell, navigation, blocked))
        return {
          valid: false,
          reason: "A runner would be trapped by that placement.",
          cells: blockedCells,
        };
    }
    return { valid: true, reason: "Route remains open", cells: blockedCells, navigation };
  }

  commitPlacement(
    towerId: string,
    cells: readonly GridCoordinate[],
    navigation?: NavigationSnapshot,
  ): NavigationSnapshot {
    this.grid.occupy(towerId, cells);
    this.revision += 1;
    this.snapshotValue = navigation ? { ...navigation, revision: this.revision } : this.calculate();
    return this.snapshotValue;
  }
  releaseTower(towerId: string): NavigationSnapshot {
    this.grid.release(towerId);
    return this.recalculate();
  }
  distanceAt(coordinate: GridCoordinate): number {
    return this.snapshotValue.distance[this.grid.index(coordinate)] ?? Number.POSITIVE_INFINITY;
  }
  nextCell(coordinate: GridCoordinate): GridCoordinate | null {
    return this.snapshotValue.next[this.grid.index(coordinate)] ?? null;
  }

  routeFrom(start: GridCoordinate, snapshot = this.snapshotValue): GridCoordinate[] {
    const route: GridCoordinate[] = [{ ...start }],
      visited = new Set([coordinateKey(start)]);
    let current = start;
    for (let step = 0; step < this.grid.cells.length; step += 1) {
      const next = snapshot.next[this.grid.index(current)] ?? null;
      if (!next) break;
      const key = coordinateKey(next);
      if (visited.has(key)) break;
      route.push(next);
      visited.add(key);
      current = next;
    }
    return route;
  }

  private hasNearbyReachableCell(
    origin: GridCoordinate,
    snapshot: NavigationSnapshot,
    blocked: ReadonlySet<string>,
  ): boolean {
    const candidates = [
      origin,
      ...DIRECTIONS.map((direction) => ({ x: origin.x + direction.x, y: origin.y + direction.y })),
    ];
    return candidates.some(
      (coordinate) =>
        this.grid.inside(coordinate) &&
        this.grid.isWalkable(coordinate, blocked) &&
        Number.isFinite(snapshot.distance[this.grid.index(coordinate)]),
    );
  }

  private calculate(
    additionallyBlocked: ReadonlySet<string> = new Set(),
    revision = this.revision,
  ): NavigationSnapshot {
    const count = this.grid.cells.length,
      distance = Array<number>(count).fill(Number.POSITIVE_INFINITY),
      next = Array<GridCoordinate | null>(count).fill(null),
      queue = new MinQueue();
    let order = 0;
    for (const exit of this.grid.map.exits) {
      const index = this.grid.index(exit.cell);
      distance[index] = 0;
      queue.push({ coordinate: exit.cell, distance: 0, order: order++ });
    }
    while (queue.size > 0) {
      const current = queue.pop()!,
        currentIndex = this.grid.index(current.coordinate);
      if (current.distance !== distance[currentIndex]) continue;
      for (const direction of DIRECTIONS) {
        const neighbor = {
          x: current.coordinate.x + direction.x,
          y: current.coordinate.y + direction.y,
        };
        if (!this.grid.isWalkable(neighbor, additionallyBlocked)) continue;
        const index = this.grid.index(neighbor),
          cell = this.grid.cell(neighbor)!,
          candidate = current.distance + cell.movementCost;
        if (candidate + Number.EPSILON < distance[index]!) {
          distance[index] = candidate;
          next[index] = current.coordinate;
          queue.push({ coordinate: neighbor, distance: candidate, order: order++ });
        }
      }
    }
    const reachableEntrances = this.grid.map.entrances
      .filter((entry) => Number.isFinite(distance[this.grid.index(entry.cell)]))
      .map((entry) => entry.id);
    return { revision, distance, next, reachableEntrances };
  }
}
